<template>
    <div class="ap-ci">
        <div class="ap-ci__container">
            <div class="ap-ci__input-container">
                <TitleText class="spacing-b-6" level="2">
                    {{ $ml.get('titleViewApplePay') }}
                </TitleText>
                <CieloSelect
                    @change="selectItem"
                    :title="$ml.get('installment')"
                    name="selectInstallment"
                    :label="$ml.get('installmentsNumber')"
                    :option-items="applePayInstallmentList || undefined"
                    :selected-item="numberOfInstallments"
                    :on-get-item-event="getLangOptions"
                    :disabled="!applePayInstallmentList.length || this.installmentIsFixed"
                />
            </div>

            <div class="ap-ci__input-container">
                <TitleText class="spacing-b-6" level="2">
                    {{ $ml.get('yourDocumentNumber') }}
                </TitleText>
                <div class="ap-ci__identity-container">
                    <CieloSelect
                        @change="selectDocumentType"
                        :title="$ml.get('documentType')"
                        :label="$ml.get('selectDocumentType')"
                        :option-items="documentTypes"
                        :selected-item="selectedDocumentType"
                        other-class="cielo-select__container--documentType"
                        :can-translate="true"
                    />

                    <CieloInput
                        style="width: 100%; margin-bottom: 0px"
                        :autofocus="false"
                        :borderless="false"
                        name="Identity"
                        :label="$ml.get('identity')"
                        v-model="Identity"
                        v-validate="identityValidationRule"
                        :type="identityInputType"
                        :mask="identityMask"
                        :data-vv-as="$ml.get('identity')"
                        :error="errors.first('Identity')"
                    />
                </div>
            </div>

            <CieloCheckBox
                v-if="isDofGuest"
                v-model="agreement"
                :clickable-slot="true"
                :text-aria="$ml.get('termsPreffix') + $ml.get('termsSuffix')"
            >
                <span v-text="$ml.get('termsPreffix')" />&nbsp;
                <a target="_blank" :href="showTerms" v-text="$ml.get('termsSuffix')" tabindex="0" />
            </CieloCheckBox>
        </div>

        <div class="ap-ci__cta-container">
            <ButtonsFinishPayment
                :disable-button-next="disableButtonFinish"
                :loading="loading"
                @click-next="finishPayment"
                @click-previous="goToPreviousStep"
            />
        </div>
    </div>
</template>

<script>
const CieloInput = () =>
	import(
			/* webpackChunkName: "cielo-input" */ '@/components/template/CieloInput'
	);

import Swal from 'sweetalert2';
import vars from '@/config/vars';
import CpfCnpjValidation from '@/helpers/validation-rules/CpfCnpjValidation';
import PaymentProcessor from '@/services/payment/PaymentProcessor';
import { mapGetters } from 'vuex';
import { Token, Options } from '@/config/lang/genericOptions.js';
import { logEvent, logException } from '@/helpers/cieloLogger';
import CieloSelect from '@/components/template/CieloSelect/CieloSelect';
import CieloCheckBox from '@/components/template/CieloCheckBox';
import ButtonsFinishPayment from '@/components/template/ButtonsFinishPayment';
import TitleText from '@/components/template/TitleText';

export default {
	components: {
		CieloSelect,
		CieloInput,
		CieloCheckBox,
		ButtonsFinishPayment,
		TitleText
	},

	data() {
		return {
			loading: false,
			showTerms: `${vars.CIELO_CHECKOUT_URL}/TransactionalVNext/Content/documents/Termos%20e%20condicoes%20de%20Uso_Cielo.pdf`,
			documentTypes: [
				{ Text: 'cpf', Value: 'CPF' },
				{ Text: 'cnpj', Value: 'CNPJ' },
				{ Text: 'foreign', Value: 'FOREIGN' },
			],
			selectedDocumentType: null,
		};
	},

	computed: {
		...mapGetters({
			applePayInstallmentList: 'payment/getApplePayInstallmentList',
			isNational: 'billingAddress/getIsNational',
			installmentIsFixed: 'payment/getSelectedPaymentMethodInstallmentIsAbleToBeFixed',
			isDofGuest: 'dataOnFile/isDofGuest',
		}),

		numberOfInstallments: {
			get: (self) =>
				self.$store.getters['payment/getInstallmentNumberOfPayments'],
			set(value) {
				this.$store.commit('payment/setNumberOfInstallments', value);
			},
		},
		Identity: {
			get: (self) => self.$store.state.customer.Identity,
			set(value) {
				this.$store.commit('customer/setIdentity', value);
			},
		},
		IdentityType: {
			get: (self) => self.$store.state.customer.IdentityType,
			set(value) {
				this.$store.commit('customer/setIdentityType', value);
			},
		},
		agreement: {
			get: (self) => self.$store.state.customer.Agreement,
			set(value) {
				this.$store.commit('customer/setAgreement', value);
			},
		},

		identityValidationRule() {
			switch (this.IdentityType) {
				case 'CPF':
					return 'required|numeric|min:11|max:11|cpf-cnpj';
				case 'CNPJ':
					return 'required|min:14|max:18|cpf-cnpj';
				default:
					return 'required|max:32';
			}
		},
		identityMask() {
			switch (this.IdentityType) {
				case 'CPF':
					return ['###.###.###-##'];
				case 'CNPJ':
					return ['XX.XXX.XXX/XXXX-XX'];
				default:
					return null;
			}
		},
		identityInputType() {
			return ['CPF', 'CNPJ'].includes(this.IdentityType) ? 'tel' : 'text';
		},

		disableButtonFinish() {
			const hasErrors = this.errors.items.length > 0;
			const missingCommonFields = !this.Identity || !this.agreement || !this.numberOfInstallments;

			return missingCommonFields || hasErrors;
		}
	},

	created() {
		this.$validator.extend('cpf-cnpj', CpfCnpjValidation);
		this.selectedDocumentType = this.IdentityType;
		PaymentProcessor.multiLanguage = this.$ml;
	},

	methods: {
		goToPreviousStep() {
			logEvent({
				category: 'apple_pay',
				action: 'troca_forma_pagamento_apay',
				label: 'Na tela de escolha de parcela usuario escolhe trocar a forma de pagamento.'
			});

			this.$store.commit('payment/setNumberOfInstallments', 0);
			this.$store.dispatch('pipeline/pushTo', 'ChangePaymentType');
		},

		selectDocumentType(value) {
			this.IdentityType = value;
		},

		async finishPayment() {
			if (!this.loading) {
				this.loading = true;

				logEvent({
					category: 'apple_pay',
					action: 'abre_modal_apay',
					label: 'Usuário define quantidade de parcela, CPF e clica no botão de finalizar compra abrindo o modal da Apple.'
				});

				try {
					await this.$store.dispatch('applePayPayment/initApplePaySession');
					await PaymentProcessor.Process();
				} catch (error) {
					if (error) {
						const messageError = error.message;
						logException(error);
						console.error(':: Finish Payment Error ::', messageError);
						Swal.fire({
							type: 'error',
							title: this.$ml.get('applePayErrorTitle'),
							text: this.$ml.get(messageError)
						});
					}
				} finally {
					this.loading = false;
				}
			}
		},


		selectItem(item) {
			this.numberOfInstallments = item;
		},

		getLangOptions(item) {
			let tokens = [];

			tokens.push(new Token('quantity', item.Value));
			tokens.push(new Token('installmentPrice', item.installmentPrice));

			return new Options('installmentsItem', tokens);
		},
	},
};
</script>
<style lang='scss' scoped>
.ap-ci {
	@media screen and (max-width: 1024px) {
		display: flex;
		flex-direction: column;
		flex: 1 0 auto;
	}

	&__container {
		margin-bottom: 32px;

		@media screen and (max-width: 1024px) {
			flex: 1;
		}
	}

	&__input-container {
		margin-bottom: 32px;
	}

	&__identity-container {
		display: flex;
		gap: 20px;
		width: 100%;

		@media screen and (max-width: 1024px) {
			flex-direction: column;
			margin-top: 0px;
		}
	}
}
</style>
